import type {
  AppMetadata,
  AppMetadataClaims,
  Auth0User,
  Permission,
  UserMetadata,
  UserMetadataClaims,
  UserPreferencesDocument
} from 'paintscout'
import React from 'react'

type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends Array<infer U>
    ? Array<RecursivePartial<U>> // eslint-disable-next-line
    : T[P] extends object
      ? RecursivePartial<T[P]>
      : T[P]
}

// legacy
export interface UserProfile {
  _id: string
  firstName: string
  lastName: string
  phoneNumber: string
  title?: string
  email: string
  preferences: UserPreferencesDocument
  roles?: string[]
  active?: boolean
  company?: AppMetadata['company']
}

export type Auth0UserWithClaims = Auth0User & {
  app_metadata?: AppMetadata & RecursivePartial<AppMetadataClaims>
  user_metadata?: UserMetadata & RecursivePartial<UserMetadataClaims>
}

export interface UserValue {
  user?: Auth0UserWithClaims
  preferences?: UserPreferencesDocument
  profile?: UserProfile
  highestRole?: string
  isDirty?: boolean
  isSuperadmin?: boolean
  isSupport?: boolean
  isAdmin?: boolean
  isSales?: boolean
  isCrew?: boolean
  isViewer?: boolean
  isCorporate?: boolean
  isCore?: boolean
  isTeam?: boolean
  isPainter?: boolean
  isOwner?: boolean
  isFieldUser?: boolean
  isOfficeUser?: boolean
  /** May refund or cancel payments — see `canReversePayments` (the API enforces the same rule). */
  canReversePayments?: boolean
  painterType?: 'subcontractor' | 'employee'
  isSubscribed?: boolean
  isChurned?: boolean
  hasPermissions?: (permissions: Permission[], any?: boolean) => boolean
  updateTrialExpired?: (date: number) => void
  isTrial?: boolean
  flaggedTrial?: boolean
  billingId?: string
  nonBilling?: boolean
  trialExpired?: boolean
  trialExpiry?: number
  /**
   * When a cancelled or paused company's temporary access window ends (ENG-2358, see
   * hasTemporaryAccess). Undefined outside a window.
   */
  temporaryAccessUntil?: number
  remainingQuotes?: number
  paymentExpiryNotice?: boolean
  isMasquerading?: boolean
  updatePaymentExpiryNotice?: (paymentExpiryNotice: boolean) => Promise<void>
  // Payment-failure dunning state (ENG-1532). 'past_due' raises the in-app notice and the header
  // banner. It blocks nothing: neither the contractor's work nor anything the homeowner can do.
  dunningStatus?: 'none' | 'past_due'
  // The rest of the episode, for the notice and banner copy. Every field can be missing even
  // while `dunningStatus` is 'past_due': the Auth0 token carries the status alone, and it is what
  // the app falls back to before the appDocs query resolves. Copy must read correctly without
  // them — no amount, no date.
  dunning?: {
    firstPaymentFailedAt?: number
    invoiceId?: string
    /** Smallest currency unit (cents), straight from Chargebee's invoice. */
    amountDue?: number
    currency?: string
    deactivationAt?: number
  }
}

const UserContext = React.createContext<UserValue>({})

export default UserContext
